import React from "react";
import {
  Box,
  Grid,
  SpaceBetween,
  Link,
  ExpandableSection,
  Table,
  Button,
  ColumnLayout,
  Container,
  ToggleButton,
} from "@amzn/awsui-components-react";
import { TEMPLATE_APP, TEMPLATE_APP_OUTPUT, TEMPLATE_APP_PERMISSION } from "../Constants";
import CustomMultiHybridInputsForAppOutputs from "./CustomMultiHybridInputsForAppOutputs";
import CustomMultiHybridInputsWithPK from "./CustomMultiHybridInputsWithPK";
import CustomMultiSelect from "./CustomMultiSelect";
import CustomInput from "./CustomInput";

const ApplicationDetailsTile = ({
  isEditable,
  getValue,
  updateValue,
  getSelectFieldValues,
  getMultiSelectedOptions,
  shouldShowMandatory,
  mandatoryFieldListener,
  accountingOrg,
  usecaseRunFrequency,
  usecaseType,
  financialRelevance,
  canEdit,
  featuredPressed,
  setFeaturedPressed,
  markFeatured,
  id,
  isNewApp,
  isEditMode
}) => {
  const renderMandatoryIndicator = () => (
    <Box color="text-status-error" fontSize="heading-l" fontWeight="bold">
      *
    </Box>
  );

  const renderOutputSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Process Output links"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.OUTPUT) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiHybridInputsForAppOutputs
            field={TEMPLATE_APP.OUTPUT}
            subKeyForSelection={TEMPLATE_APP.OUTPUT_SUB.TYPE}
            mandatoryFieldListener={mandatoryFieldListener}
            updateValue={updateValue}
            getOptions={getSelectFieldValues}
            getValues={getValue}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="s">
          {getValue(TEMPLATE_APP.OUTPUT) ? (
            <Table
              columnDefinitions={[
                {
                  id: "type",
                  header: "Type",
                  cell: item => item[TEMPLATE_APP_OUTPUT.TYPE],
                  isRowHeader: true
                },
                {
                  id: "value",
                  header: "Link",
                  cell: item => (
                    <Link href={item[TEMPLATE_APP_OUTPUT.VALUE]} external>
                      {item.hasOwnProperty(TEMPLATE_APP_OUTPUT.LABEL) ?
                        item[TEMPLATE_APP_OUTPUT.LABEL] :
                        item[TEMPLATE_APP_OUTPUT.TYPE]
                      }
                    </Link>
                  ),
                  sortingField: "alt"
                }
              ]}
              items={getValue(TEMPLATE_APP.OUTPUT)}
              loadingText="Loading output details"
              sortingDisabled
              empty={
                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                  <SpaceBetween size="m">
                    <Box variant="strong">No resources</Box>
                  </SpaceBetween>
                </Box>
              }
              variant="embedded"
            />
          ) : (
            <Box textAlign="left" color="text-body-secondary">
              No output links configured
            </Box>
          )}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderPermissionsSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Permissions"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.PERMISSION) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiHybridInputsWithPK
            field={TEMPLATE_APP.PERMISSION}
            subKeys={[
              { subKey: TEMPLATE_APP_PERMISSION.NAME, placeholder: 'Name/Description' },
              { subKey: TEMPLATE_APP_PERMISSION.LINK, placeholder: 'URL/Link' }
            ]}
            pk={TEMPLATE_APP_PERMISSION.APP_PERMISSION_PK}
            updateValue={updateValue}
            getValues={getValue}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {getValue(TEMPLATE_APP.PERMISSION)?.map((item, index) => (
            item.hasOwnProperty(TEMPLATE_APP_PERMISSION.LINK) ? (
              <Box key={index} variant="p">
                <Link href={item.link} external>
                  {item.name ? item.name : item.link}
                </Link>
              </Box>
            ) : null
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderOrganizationsSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Organizations Supported"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.ORGANIZATION) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiSelect
            field={TEMPLATE_APP.ORGANIZATION}
            updateValue={updateValue}
            getValues={getSelectFieldValues}
            getSelectedOptions={getMultiSelectedOptions}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {accountingOrg?.map((item) => (
            <Box key={item} variant="p">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderRunFrequencySection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Run Frequency"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.CADENCE) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiSelect
            field={TEMPLATE_APP.CADENCE}
            updateValue={updateValue}
            getValues={getSelectFieldValues}
            getSelectedOptions={getMultiSelectedOptions}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {usecaseRunFrequency?.map((item) => (
            <Box key={item} variant="p">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderContactSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Contact DEV TEAM for this Process"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.SIM_TICKET_URL) ? (
        <SpaceBetween size="s">
          <CustomInput
            field={TEMPLATE_APP.SIM_TICKET_URL}
            placeholder="SIM ticket url"
            updateValue={updateValue}
            getValue={getValue}
          />
          <CustomInput
            field={TEMPLATE_APP.SIM_TICKET_LABEL}
            placeholder="label"
            updateValue={updateValue}
            getValue={getValue}
          />
        </SpaceBetween>
      ) : (
        <Box>
          {getValue(TEMPLATE_APP.SIM_TICKET_URL) ? (
            <Button
              href={getValue(TEMPLATE_APP.SIM_TICKET_URL)}
              iconAlign="right"
              iconName="external"
              target="_blank"
            >
              {getValue(TEMPLATE_APP.SIM_TICKET_LABEL) ?
                getValue(TEMPLATE_APP.SIM_TICKET_LABEL) :
                'Raise a ticket'
              }
            </Button>
          ) : (
            <Box color="text-body-secondary">
              Not Available
            </Box>
          )}
        </Box>
      )}
    </ExpandableSection>
  );

  const renderUseCaseTypeSection = () => (
    <ExpandableSection
      variant="borderless"
      defaultExpanded={true}
      headerText="Use Case Type"
    >
      {isEditable(TEMPLATE_APP.USE_CASE) ? (
        <CustomMultiSelect
          field={TEMPLATE_APP.USE_CASE}
          updateValue={updateValue}
          getValues={getSelectFieldValues}
          getSelectedOptions={getMultiSelectedOptions}
        />
      ) : (
        <SpaceBetween size="xs">
          {usecaseType?.map((item) => (
            <Box key={item} variant="p">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderFinancialRelevanceSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Financial Relevance"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.FINANCIAL_RELEVANCE) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiSelect
            field={TEMPLATE_APP.FINANCIAL_RELEVANCE}
            updateValue={updateValue}
            getValues={getSelectFieldValues}
            getSelectedOptions={getMultiSelectedOptions}
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {financialRelevance?.map((item) => (
            <Box key={item} variant="p">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  return (
    <Box padding={{ left: "l" }}>
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
          {renderOutputSection()}

          {renderPermissionsSection()}

          {renderOrganizationsSection()}

          {renderRunFrequencySection()}
          {renderContactSection()}
          {renderUseCaseTypeSection()}
          {renderFinancialRelevanceSection()}
        </ColumnLayout>
      </SpaceBetween>
    </Box>
  );
};

export default ApplicationDetailsTile;
